import Button from '@components/Button';
import { CheckboxNoFunc } from '@components/Checkbox';
import Image from '@components/Image';
import Modal from '@components/Modal';
import { sendWarning } from '@components/Noti';
import TextLink from '@components/TextLink';
import Title from '@components/Title';
import IconTrash from '@components/icons/IconTrash';
import IconVip from '@components/icons/IconVip';
import { API_DOMAIN, VIP_URL } from '@constants';
import { uploadWatermark } from '@modules/alioss';
import { queryClient } from '@queryClient';
import { $isOwner, $isTeamAccount, $isVip } from '@stores/memberStores';
import { ImageType } from '@types';
import addComponent from '@utils/addComponent';
import axios from '@utils/axios';
import getImageSize from '@utils/getImageSize';
import selectFile from '@utils/selectFile';
import { formatSize } from 'pano-utils';
import { useState } from 'react';
import { QueryClientProvider, useMutation, useQuery } from 'react-query';
import { useLocalStorage } from 'react-use';

interface Props {
  customEnabled?: boolean;
  onRequestClose: () => void;
  onConfirm: (payload: { watermarked: boolean; watermarkUrl: string }) => void;
}

type OPTION = 0 | 1 | 2;

function WartermarkModal({
  customEnabled = false,
  onRequestClose,
  onConfirm,
}: Props) {
  const [option, setOption] = useLocalStorage<OPTION>(
    'watermark-default-option',
    1
  );

  const {
    data: watermarkData,
    isLoading,
    refetch,
  } = useQuery(
    ['member-watermark'],
    () => {
      const isOwner = $isOwner.getState();
      const isTeamAccount = $isTeamAccount.getState();
      const url =
        isTeamAccount && !isOwner
          ? `${API_DOMAIN}/team/member/watermark`
          : `${API_DOMAIN}/member/my/watermark`;
      return axios.get<{ url: string }>(url);
    },
    {
      onSuccess: (data) => {
        if (!data.url) {
          setOption(1);
        }
      },
    }
  );
  const watermarkUrl = watermarkData?.url ?? '';

  const handleCustomClick = async () => {
    if (option === 2) return;
    if (!$isVip.getState()) {
      showVipModal();
      return;
    }
    if (watermarkUrl) {
      setOption(2);
      return;
    }
    const { url } = await showCustomWatermarkModal({
      defaultUrl: watermarkUrl,
    });
    if (url) {
      refetch();
      setOption(2);
    }
  };

  if (isLoading) return null;

  const uploadDisabled = isLoading || (option === 2 && !watermarkUrl);

  return (
    <Modal
      isOpen
      header={<Title>版权保护提醒</Title>}
      onRequestClose={onRequestClose}
    >
      <div className="flex h-full flex-col items-center justify-center">
        <div className="text-lg">为保护版权利益，建议为上传的素材打上水印</div>
        <TextLink
          className="mt-10"
          href="https://www.720yun.com/t/cd7j55saem9"
          target="_blank"
        >
          查看水印效果
        </TextLink>
        <Button
          className="mt-30 w-[176px] h-[38px]"
          disabled={uploadDisabled}
          onClick={() => {
            onConfirm({
              watermarked: option !== 0,
              watermarkUrl: option === 2 && watermarkUrl ? watermarkUrl : '',
            });
          }}
        >
          上传素材
        </Button>
        <div className="flex items-center gap-20 mt-20 select-none">
          <div
            className="flex items-center cursor-pointer"
            onClick={() => setOption(0)}
          >
            <CheckboxNoFunc checked={!isLoading && option === 0} />
            <div className="ml-5">不打水印</div>
          </div>
          <div
            className="flex items-center cursor-pointer"
            onClick={() => setOption(1)}
          >
            <CheckboxNoFunc checked={!isLoading && option === 1} />
            <div className="ml-5">系统水印</div>
          </div>
          {customEnabled && (
            <div className="flex items-center">
              <div
                className="flex items-center cursor-pointer"
                onClick={handleCustomClick}
              >
                <CheckboxNoFunc checked={!isLoading && option === 2} />
                <div className="ml-5 flex items-center">
                  <IconVip className="mr-2" />
                  自定义水印
                </div>
              </div>
              {!isLoading && (
                <TextLink
                  className="ml-10"
                  onClick={async () => {
                    if (!$isVip.getState()) {
                      showVipModal();
                      return;
                    }
                    const { url } = await showCustomWatermarkModal({
                      defaultUrl: watermarkUrl,
                    });
                    refetch();
                    if (url) {
                      setOption(2);
                    }
                  }}
                >
                  {watermarkUrl ? '已设置' : '未设置'}
                </TextLink>
              )}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}

function showVipModal() {
  addComponent((remove) => {
    return (
      <Modal
        isOpen
        header={<Title>自定义水印</Title>}
        footer={
          <Button as="a" href={VIP_URL} target="_blank">
            去升级
          </Button>
        }
        cancelText="取消"
        onRequestClose={remove}
      >
        <div className="relative flex h-full flex-col items-center justify-center">
          <div className="mb-10 text-lg">升级个人专业版/企业版后即可使用</div>
          <div className="text-center">
            该功能支持在全景图片/高清矩阵上打上自定义的水印保护用户版权利益，同时强化自身品牌露出
          </div>
        </div>
      </Modal>
    );
  });
}

const MAX_SIZE = 500 * 1024; // 500KB
const WATERMARK_SIZE = 300;

function WatermarkSettingModal({
  defaultUrl = '',
  onRequestClose,
  onConfirm,
}: {
  defaultUrl?: string;
  onRequestClose: () => void;
  onConfirm: (url: string) => void;
}) {
  const [url, setUrl] = useState(defaultUrl);

  const { mutateAsync, isLoading } = useMutation(
    ['member-watermark-save'],
    (watermarkUrl: string) => {
      const isOwner = $isOwner.getState();
      const isTeamAccount = $isTeamAccount.getState();
      const requestUrl =
        isTeamAccount && !isOwner
          ? `${API_DOMAIN}/team/member/watermark`
          : `${API_DOMAIN}/member/my/watermark`;
      return axios.post(requestUrl, { url: watermarkUrl });
    }
  );

  const handleUpload = async () => {
    const files = await selectFile({ accept: 'image/png', multiple: false });
    const image = files[0];
    if (!image) return;
    if (image.size > MAX_SIZE) {
      sendWarning(`图片大小已超过${formatSize(MAX_SIZE)}, 请重新选择`);
      return;
    }
    const imageSize = await getImageSize(image);
    if (
      imageSize?.width !== WATERMARK_SIZE ||
      imageSize?.height !== WATERMARK_SIZE
    ) {
      sendWarning(
        `仅支持${WATERMARK_SIZE}*${WATERMARK_SIZE}的图片, 请重新选择`
      );
      return;
    }
    const url = await uploadWatermark({ file: files[0] });
    setUrl(url);
  };

  const handleSave = async () => {
    await mutateAsync(url);
    onConfirm(url);
    onRequestClose();
  };

  return (
    <Modal
      isOpen
      invisibleBackdrop
      header={<Title>设置自定义水印</Title>}
      footer={
        <Button disabled={isLoading} onClick={handleSave}>
          保存水印
        </Button>
      }
      cancelText="取消保存"
      onRequestClose={onRequestClose}
    >
      <div className="relative flex h-full items-center justify-center">
        <div className="relative w-150 h-150 bg-[#efefef] flex items-center justify-center">
          {url ? (
            <>
              <Image
                className="w-full h-full"
                src={`/${url}`}
                type={ImageType.watermark}
                objectFit="contain"
              />
              <div
                className="absolute top-10 right-10 p-5 bg-[rgba(0,0,0,0.5)] cursor-pointer"
                onClick={() => setUrl('')}
              >
                <IconTrash active />
              </div>
            </>
          ) : (
            <Image
              className="w-60 h-60"
              src="/imgs/placeholder.7e2e67524b2a58d5bddd1e63971c354f.png"
              type={ImageType.sysMedia}
            />
          )}
        </div>
        <div className="ml-20 text-[#909090]">
          <Button className="mb-10" onClick={handleUpload}>
            上传图片
          </Button>
          <div>{`尺寸: ${WATERMARK_SIZE}*${WATERMARK_SIZE}`}</div>
          <div>大小: {formatSize(MAX_SIZE)}</div>
          <div>格式: PNG</div>
        </div>
      </div>
    </Modal>
  );
}

function showCustomWatermarkModal({ defaultUrl }: { defaultUrl?: string }) {
  return new Promise<{ url: string }>((resolve) => {
    addComponent((remove) => {
      return (
        <QueryClientProvider client={queryClient}>
          <WatermarkSettingModal
            defaultUrl={defaultUrl}
            onConfirm={(url) => {
              resolve({ url });
              remove();
            }}
            onRequestClose={() => {
              resolve({ url: '' });
              remove();
            }}
          />
        </QueryClientProvider>
      );
    });
  });
}

export default WartermarkModal;
